/**
 * Public org-branded landing — `/org/<slug>`. Fetches the org's public profile
 * and renders the shared TicketStub. When the org has a default squad, the
 * primary CTA routes into the wc-invite waiting room. When it doesn't, the
 * stub shows the "not set up yet" notice in place of a CTA.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, Text } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useEntryIntent } from '../../../src/onboarding/hooks/useEntryIntent';
import { ENTRY_INTENT_KIND } from '../../../src/onboarding/types/entryIntent.types';
import {
  fetchPublicOrgBySlug,
  type PublicOrgLookupResponse,
} from '../../../src/onboarding/helpers/onboardingPublicApi';
import { buildPublicSignInHref } from '../../../src/signIn/helpers/postAuthRedirect.helper';
import { logger } from '../../../src/helpers/logger.helper';
import { detectMobileWebOs } from '../../../src/onboarding/helpers/detectMobileWebOs.helper';
import { TicketStub } from '../TicketStub/TicketStub';
import { SHOW_CONTINUE_WITH_APP_CTA } from '../TicketStub/TicketStub.constants';
import type { TicketStubBrand } from '../TicketStub/TicketStub.types';
import { getOrgBrandedLandingStyles } from './OrgBrandedLanding.styles';
import {
  getOrgBrandedLandingKicker,
  getOrgBrandedLandingNotSetUpNotice,
  getOrgBrandedLandingTitle,
  ORG_BRANDED_LANDING_COPY,
} from './OrgBrandedLanding.constants';

interface OrgBrandedLandingProps {
  slug: string;
  /** When set, CTAs use this returnUrl instead of the org's WC squad join path. */
  overrideReturnUrl?: string;
}

export function OrgBrandedLanding({ slug, overrideReturnUrl }: OrgBrandedLandingProps) {
  const router = useRouter();
  const theme = useTheme();
  const styles = useMemo(() => getOrgBrandedLandingStyles(theme), [theme]);
  const { setIntent } = useEntryIntent();

  const [org, setOrg] = useState<PublicOrgLookupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsResolving(true);
    setError(null);
    void (async () => {
      try {
        const data = await fetchPublicOrgBySlug(slug);
        if (cancelled) return;
        setOrg(data);
        // Skip setting WC intent when called from the join-code flow (overrideReturnUrl)
        // so the regular tutorial is shown rather than the 2-slide WC variant.
        if (!overrideReturnUrl) {
          setIntent({
            kind: ENTRY_INTENT_KIND.ORG,
            orgSlug: slug,
            inviteCode: data.defaultGroup?.inviteCode,
            groupId: data.defaultGroup?.id,
          });
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : ORG_BRANDED_LANDING_COPY.ERROR_TITLE_FALLBACK;
        logger.warn('org landing resolve failed', { message, slug });
        setError(message);
      } finally {
        if (!cancelled) setIsResolving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, setIntent, overrideReturnUrl]);

  // Goes straight to sign-up with the org's default-squad invite queued as
  // `returnUrl`. We deliberately *do not* hop through /wc-invite — that would
  // render a second ticket stub for the same squad and feel redundant.
  const buildReturnUrl = useCallback(() => {
    if (overrideReturnUrl) return overrideReturnUrl;
    const code = org?.defaultGroup?.inviteCode;
    return code
      ? `/app/worldcup/groups/join?code=${encodeURIComponent(code.toUpperCase())}&autojoin=1`
      : '/';
  }, [org, overrideReturnUrl]);

  const handleJoin = useCallback(() => {
    if (!org?.defaultGroup?.inviteCode) return;
    router.replace(buildPublicSignInHref({ signup: true, returnUrl: buildReturnUrl() }) as `/${string}`);
  }, [buildReturnUrl, org, router]);

  const handleSignIn = useCallback(() => {
    router.replace(buildPublicSignInHref({ returnUrl: buildReturnUrl() }) as `/${string}`);
  }, [buildReturnUrl, router]);

  const fallbackGradient = useMemo<[string, string]>(
    () => [
      org?.primaryColor || theme.primary,
      org?.secondaryColor || theme.primaryLight || theme.primary,
    ],
    [org, theme],
  );

  const hoverWeb = Platform.select({ web: WEB_HOVER_TRANSITION_STYLE });

  if (isResolving) {
    return (
      <LinearGradient colors={fallbackGradient} style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.white} />
        <Text style={styles.loadingText}>{ORG_BRANDED_LANDING_COPY.LOADING}</Text>
      </LinearGradient>
    );
  }

  if (error || !org) {
    return (
      <LinearGradient colors={fallbackGradient} style={styles.loadingContainer}>
        <Text style={styles.errorTitle}>{error || ORG_BRANDED_LANDING_COPY.ERROR_TITLE_FALLBACK}</Text>
        <Text style={styles.errorMessage}>
          {ORG_BRANDED_LANDING_COPY.ERROR_MESSAGE}
        </Text>
        <Pressable
          onPress={() => router.replace('/' as `/${string}`)}
          style={({ pressed }) => [styles.primaryButton, hoverWeb, pressed && styles.pressed]}
        >
          <Text style={styles.primaryButtonText}>{ORG_BRANDED_LANDING_COPY.GO_HOME_LABEL}</Text>
        </Pressable>
      </LinearGradient>
    );
  }

  const displayName = org.displayName || org.name;
  const hasDefaultGroup = Boolean(org.defaultGroup?.inviteCode);
  const brand: TicketStubBrand = {
    name: displayName,
    primaryColor: org.primaryColor || theme.primary,
    secondaryColor: org.secondaryColor ?? null,
    logoUrl: org.logoUrl ?? null,
  };

  return (
    <TicketStub
      brand={brand}
      presentedBy={`${ORG_BRANDED_LANDING_COPY.PRESENTED_BY_PREFIX}${displayName}`}
      kicker={getOrgBrandedLandingKicker(displayName)}
      title={getOrgBrandedLandingTitle(org.slug, displayName)}
      subtitle={org.description?.trim() || ORG_BRANDED_LANDING_COPY.DEFAULT_SUBTITLE}
      memberCount={0}
      memberCountLabel={ORG_BRANDED_LANDING_COPY.MEMBER_COUNT_LABEL}
      groupCode={org.defaultGroup?.inviteCode ?? null}
      primaryCta={
        hasDefaultGroup
          ? {
              label: ORG_BRANDED_LANDING_COPY.JOIN_CTA_LABEL,
              testID: ORG_BRANDED_LANDING_COPY.JOIN_CTA_TEST_ID,
              onPress: handleJoin,
            }
          : null
      }
      disabledNotice={hasDefaultGroup ? null : getOrgBrandedLandingNotSetUpNotice()}
      secondaryCta={{
        label: ORG_BRANDED_LANDING_COPY.SIGN_IN_LABEL,
        testID: ORG_BRANDED_LANDING_COPY.SIGN_IN_TEST_ID,
        onPress: handleSignIn,
      }}
      showOpenInApp={
        SHOW_CONTINUE_WITH_APP_CTA && detectMobileWebOs() !== null
      }
    />
  );
}
